import React, { useState, useRef, useEffect } from 'react'
import {
  Typography,
  Checkbox,
  Button,
  Row,
  Col,
  Card,
  message,
  Spin,
  Modal,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons'
import _ from 'lodash'
import TurnstileWidget from '../../component/common/turnstile.widget'
import { useParams } from 'react-router-dom'
import { addRegisterPatient } from '../../store/action/patient.action'
import { connect } from 'react-redux'
import moment from 'moment'
import '../../css/patient_registration.scss'


const Review = (props) => {
  const { Title, Text, Link } = Typography
  const [emailChecked, setEmailChecked] = useState(false)
  const [recapcha, setRecapcha] = useState(false)
  const [loading, setSpin] = useState(false)
  const capchaResetRef = useRef(null)
  const capchaTokenRef = useRef(null)
  const params = useParams()

  // See account.delete.form.js — prefer the callback's token, fall back to
  // reading the widget. Especially important here: the Submit button used to be
  // disabled on `!recapcha`, so a callback that never fired left the patient
  // with a dead button and nothing explaining why.
  const currentCaptchaToken = () =>
    recapcha || (capchaTokenRef.current ? capchaTokenRef.current() : null)
  const customerid = params.customer_id

  useEffect(() => {
    window.scrollTo(0, 0)
    // Turnstile renders itself on mount and delivers its token through
    // TurnstileWidget's onToken, so there is nothing to kick off here. The old
    // reCAPTCHA needed an explicit executeAsync() on load — which never
    // resolved once the site key went bad, leaving the submit button
    // permanently disabled behind `!recapcha`.
  }, [])

  var showConfirmDeleteBox = (msg) => {
    Modal.confirm({
      title: msg,
      icon: <ExclamationCircleOutlined />,
      okText: 'Ok',
      cancelButtonProps: { hidden: 'true' },
      onOk: () => {},
    })
  }

  const handleSubmit = async () => {
    try {
      const captchaToken = currentCaptchaToken()
      if (!captchaToken) {
        message.error('Please complete the security check and try again.')
        return
      }
      const { consent, patient } = props.registerationState
      let patientData = {
        ...patient,
        consent: { ...consent, signature_time: moment().utc() },
        phone: patient.phone.toString(),
        customer: customerid,
        token: captchaToken,
        consent_email: emailChecked,
      }
      patientData?.caregiver?.name && delete patientData.caregiver.name
      setSpin(true)
      const data = await props.addRegisterPatient(patientData)
      // Single-use token — reset so a retry after a failure gets a fresh one.
      capchaResetRef.current && capchaResetRef.current()
      if (data.status) {
        // message.success('Thankyou for creating patient authorization form')
        props.onSubmit()
      } else {
        if (data.message && data.message.trim() !== '')
          showConfirmDeleteBox(data.message)
      }
      setSpin(false)
    } catch (error) {
      console.log(error)
    }
  }
  const handleCheckBox = (checked) => {
    setEmailChecked(checked)
  }
  const convertInchToDecimalFeet = (inches) => {
    return `${Math.floor(inches / 12)}'${inches % 12}"`
  }

  const renderReview = () => {
    const {
      firstname,
      lastname,
      gender,
      height,
      phone,
      dob,
      email,
      shipaddress,
      caregiver,
    } = props.registerationState.patient
    const { signatures } = props.registerationState.consent

    return (
      <Row gutter={[16, 16]}>
        {/* Patient Details */}
        <Col span={24}>
          <Title level={5}>Patient Details</Title>
          <Text strong>Name:</Text>{' '}
          <Text>{firstname + ' ' + lastname || ''}</Text>
          <br />
          <Text strong>DOB:</Text> <Text>{dob || ''}</Text>
          <br />
          <Text strong>Gender:</Text> <Text>{_.upperFirst(gender) || ''}</Text>
          <br />
          <Text strong>Height:</Text>{' '}
          <Text>{convertInchToDecimalFeet(height) || ''}</Text>
          <br />
          <Text strong>Phone:</Text> <Text>{phone || ''}</Text>
          <br />
          <Text strong>Email:</Text> <Text>{email || ''}</Text>
          <br />
          <Text strong>Address:</Text>
          <br />
          <Text> {shipaddress?.addressline1},</Text>
          <br />
          {shipaddress?.addressline2 && (
            <>
              {' '}
              <Text> {shipaddress?.addressline2},</Text> <br />
            </>
          )}
          <Text>
            {' '}
            {shipaddress?.city +
              ' , ' +
              shipaddress?.state +
              ' , ' +
              shipaddress?.country +
              ' , ' +
              shipaddress?.zip}
          </Text>
        </Col>

        {/* Caregiver Details */}
        <Col span={24}>
          <Title level={5}>Caregiver Details</Title>
          <Text strong>Name:</Text>{' '}
          <Text>
            {(caregiver?.fname || 'NA') + ' ' + (caregiver?.lname || '')}
          </Text>
          <br />
          <Text strong>Phone:</Text> <Text>{caregiver?.phone || 'NA'}</Text>
          <br />
          <Text strong>Email:</Text> <Text>{caregiver?.email || 'NA'}</Text>
          <br />
          <Text strong>Relation:</Text>{' '}
          <Text>{caregiver?.relationship || 'NA'}</Text>
          <br />
        </Col>

        {/* Consent Provided */}
        <Col span={24}>
          <Title level={5}>Consent Provided</Title>
          <ul className='consent-ul'>
            {props.consentForms.map((consentForm, index) => {
              return (
                <li>
                  <Text type={signatures[index] ? 'success' : 'danger'}>
                    {consentForm.title}
                  </Text>
                </li>
              )
            })}
          </ul>
        </Col>
      </Row>
    )
  }
  // (the old reCAPTCHA onChange handler lived here — TurnstileWidget calls
  // setRecapcha directly via its onToken prop)
  return (
    <Spin spinning={loading}>
      <Card bordered={false}>
        <Title level={4}> REVIEW DETAILS</Title>

        {!_.isEmpty(props.registerationState) && renderReview()}

        <Row justify='end' style={{ width: '100%', gap: '5px' }}>
          <Col span={24}>
            <Row justify='end'>
              <Checkbox onChange={(e) => handleCheckBox(e.target.checked)}>
                Email copy of the forms to me
              </Checkbox>
            </Row>
          </Col>
          <Col>
            <TurnstileWidget
              onToken={setRecapcha}
              resetRef={capchaResetRef}
              tokenRef={capchaTokenRef}
            />
          </Col>
          <Col span={24}>
            <Row justify='end' gutter={25}>
              <Col xs={12} md={6}>
                <Button
                  type='secondary'
                  onClick={() => props.navigateBack()}
                  className='form-button'
                >
                  Back
                </Button>
              </Col>
              <Col xs={12} md={6}>
                <Button
                  type='primary'
                  onClick={handleSubmit}
                  className='form-button'
                >
                  Submit
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        <br />
        <br />
        <br />
      </Card>
    </Spin>
  )
}
const mapStateToProps = (state) => {
  return {}
}
const mapDispatchToProps = (dispatch) => {
  return {
    addRegisterPatient: (input) => dispatch(addRegisterPatient(input)),
  }
}
export default connect(mapStateToProps, mapDispatchToProps)(Review)
